import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiCheckCircle, FiCreditCard, FiSave, FiAlertCircle, FiChevronDown } from 'react-icons/fi';
import { WHATSAPP_AGING_BUCKETS, WHATSAPP_TEMPLATE_VARS, DEFAULT_WHATSAPP_TEMPLATES } from '../utils/whatsappTemplates';
import {
  isCollectionPaymentConfigured,
  normalizePaymentLink,
  paymentLinkInputValue,
} from '../utils/collectionPaymentSetup';
import PaymentAccountVerificationPanel from './PaymentAccountVerificationPanel';
import ContextualAssistantStrip from './ContextualAssistantStrip';

function SectionHeading({ title, description, textPri, textMuted }) {
  return (
    <div className="mb-4">
      <h3 className={`text-sm font-semibold ${textPri}`}>{title}</h3>
      {description && <p className={`text-xs mt-1 leading-relaxed ${textMuted}`}>{description}</p>}
    </div>
  );
}

export default function CollectionPaymentSetupForm({
  notificationForm,
  setNotificationForm,
  onSubmit,
  saving,
  secureFetch,
  hasProfesional = false,
  modoOscuro,
  inputClass,
  textPri,
  textMuted,
  box,
}) {
  const configured = isCollectionPaymentConfigured(notificationForm);
  const verified = Boolean(notificationForm.paymentAccountConfirmed);
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const divide = modoOscuro ? 'border-gray-700' : 'border-gray-200';
  const sectionMuted = modoOscuro ? 'bg-gray-800/30' : 'bg-gray-50/80';

  return (
    <div className={`${box} rounded-2xl border overflow-hidden xl:col-span-2`}>
      {secureFetch && (
        <div className={`px-4 pt-3 sm:px-5 ${hasProfesional ? '' : 'hidden'}`}>
          <ContextualAssistantStrip
            contextId="cuenta-cobro"
            secureFetch={secureFetch}
            hasProfesional={hasProfesional}
            modoOscuro={modoOscuro}
            textSec={textMuted}
            textMuted={textMuted}
          />
        </div>
      )}
      <div className={`px-4 py-3 sm:px-5 sm:py-4 border-b ${divide}`}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span
              className={`shrink-0 p-2 rounded-xl ${
                modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <FiCreditCard className="w-5 h-5" />
            </span>
            <div>
              <h2 className={`text-sm font-semibold ${textPri}`}>Datos para cobrar</h2>
              <p className={`text-xs mt-0.5 max-w-xl ${textMuted}`}>
                Medios de pago que ven tus clientes en Cobrar, WhatsApp, emails y PDF.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 self-start">
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold shrink-0 ${
                configured
                  ? modoOscuro ? 'bg-gray-800 text-gray-200' : 'bg-gray-100 text-gray-700'
                  : modoOscuro ? 'bg-amber-900/40 text-amber-200' : 'bg-amber-50 text-amber-800'
              }`}
            >
              {configured ? 'Datos cargados' : (
                <>
                  <FiAlertCircle className="w-3.5 h-3.5" />
                  Pendiente
                </>
              )}
            </span>
            {verified && (
              <span
                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                  modoOscuro ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-800'
                }`}
              >
                <FiCheckCircle className="w-3.5 h-3.5" />
                Titular verificado
              </span>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={onSubmit}>
        {/* Nivel 1 — esencial */}
        <section className={`px-4 py-5 sm:px-5 border-b ${divide}`}>
          <SectionHeading
            title="Medios de pago"
            description="Completá al menos uno: transferencia (alias o CBU) o link de Mercado Pago para el botón en Cobrar."
            textPri={textPri}
            textMuted={textMuted}
          />
          <div className="mb-4">
            <label className={`block text-xs font-medium mb-1.5 ${textPri}`}>
              Titular de la cuenta (como figura en el banco o Mercado Pago)
            </label>
            <input
              type="text"
              value={notificationForm.paymentHolderName || ''}
              onChange={(e) => setNotificationForm((p) => ({
                ...p,
                paymentHolderName: e.target.value,
                paymentAccountConfirmed: false,
              }))}
              className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
              placeholder="Ej. Andrea Barreda o Mi Empresa SRL"
              autoComplete="name"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${textMuted}`}>Alias</label>
              <input
                type="text"
                value={notificationForm.paymentAlias}
                onChange={(e) => setNotificationForm((p) => ({
                  ...p,
                  paymentAlias: e.target.value,
                  paymentAccountConfirmed: false,
                }))}
                className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                placeholder="miempresa.mp"
                autoComplete="off"
              />
            </div>
            <div>
              <label className={`block text-xs font-medium mb-1.5 ${textMuted}`}>CBU / CVU</label>
              <input
                type="text"
                inputMode="numeric"
                value={notificationForm.paymentCbu}
                onChange={(e) => setNotificationForm((p) => ({
                  ...p,
                  paymentCbu: e.target.value,
                  paymentAccountConfirmed: false,
                }))}
                className={`w-full rounded-xl border px-3 py-2 text-sm tabular-nums ${inputClass}`}
                placeholder="000000310001..."
                autoComplete="off"
              />
            </div>
            <div className="sm:col-span-2 lg:col-span-1">
              <label className={`block text-xs font-medium mb-1.5 ${textMuted}`}>Link de pago</label>
              <div
                className={`flex rounded-xl border overflow-hidden text-sm ${
                  modoOscuro ? 'border-gray-600' : 'border-gray-300'
                }`}
              >
                <span
                  className={`shrink-0 px-2.5 py-2 tabular-nums select-none ${
                    modoOscuro ? 'bg-gray-800 text-gray-400 border-r border-gray-600' : 'bg-gray-100 text-gray-500 border-r border-gray-300'
                  }`}
                >
                  https://
                </span>
                <input
                  type="text"
                  inputMode="url"
                  autoComplete="off"
                  value={paymentLinkInputValue(notificationForm.paymentLink)}
                  onChange={(e) => setNotificationForm((p) => ({
                    ...p,
                    paymentLink: normalizePaymentLink(e.target.value),
                  }))}
                  onBlur={(e) => setNotificationForm((p) => ({
                    ...p,
                    paymentLink: normalizePaymentLink(e.target.value),
                  }))}
                  onPaste={(e) => {
                    const pasted = e.clipboardData.getData('text');
                    if (!pasted) return;
                    e.preventDefault();
                    setNotificationForm((p) => ({
                      ...p,
                      paymentLink: normalizePaymentLink(pasted),
                    }));
                  }}
                  className={`min-w-0 flex-1 border-0 px-3 py-2 focus:ring-0 focus:outline-none ${
                    modoOscuro ? 'bg-gray-900 text-white placeholder-gray-500' : 'bg-white text-gray-900 placeholder-gray-400'
                  }`}
                  placeholder="mpago.la/tu-link"
                />
              </div>
              <p className={`mt-1 text-[10px] ${textMuted}`}>
                Podés pegar el link tal como lo copiás de Mercado Pago; agregamos https automáticamente.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <PaymentAccountVerificationPanel
              secureFetch={secureFetch}
              paymentAlias={notificationForm.paymentAlias}
              paymentCbu={notificationForm.paymentCbu}
              paymentHolderName={notificationForm.paymentHolderName}
              modoOscuro={modoOscuro}
              textPri={textPri}
              textMuted={textMuted}
            />
          </div>
          <label className={`mt-4 flex items-start gap-3 cursor-pointer max-w-2xl ${textPri}`}>
            <input
              type="checkbox"
              checked={Boolean(notificationForm.paymentAccountConfirmed)}
              onChange={(e) => setNotificationForm((p) => ({
                ...p,
                paymentAccountConfirmed: e.target.checked,
              }))}
              className="mt-0.5 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
            />
            <span className={`text-xs leading-relaxed ${textMuted}`}>
              <span className={`font-semibold ${textPri}`}>Confirmo que este titular y estos datos son de mi negocio.</span>
              {' '}Contsuite no puede consultar el banco por vos: validamos formato y coincidencia con tu perfil; la titularidad la confirmás vos en tu app.
            </span>
          </label>
        </section>

        {hasProfesional && (
          <section className={`px-4 py-5 sm:px-5 border-b ${divide} ${sectionMuted}`}>
            <SectionHeading
              title="Recordatorios automáticos"
              description="Opcional. Usan los medios de pago que cargaste arriba."
              textPri={textPri}
              textMuted={textMuted}
            />
            <label className="flex items-start gap-3 cursor-pointer max-w-2xl">
              <input
                type="checkbox"
                checked={notificationForm.collectionSequenceEnabled}
                onChange={(e) => setNotificationForm((p) => ({ ...p, collectionSequenceEnabled: e.target.checked }))}
                className="mt-0.5 rounded border-gray-300 text-[#1F80FF] focus:ring-[#1F80FF]"
              />
              <span>
                <span className={`block text-sm font-medium ${textPri}`}>Secuencia de cobranza por email</span>
                <span className={`block text-xs mt-0.5 ${textMuted}`}>
                  PDF adjunto a los 3, 7 y 15 días de vencido (si el cliente tiene email). Plan Profesional+.
                </span>
              </span>
            </label>
          </section>
        )}

        {/* Nivel 3 — personalización */}
        <section className={`px-4 py-4 sm:px-5 border-b ${divide}`}>
          <button
            type="button"
            onClick={() => setWhatsappOpen((o) => !o)}
            className={`w-full flex items-center justify-between gap-2 text-left py-1 ${textPri}`}
          >
            <span>
              <span className="block text-sm font-semibold">Plantillas de WhatsApp</span>
              <span className={`block text-xs font-normal mt-0.5 ${textMuted}`}>
                Opcional · variables {WHATSAPP_TEMPLATE_VARS.map((v) => `{{${v}}}`).join(', ')}
              </span>
            </span>
            <FiChevronDown
              className={`w-5 h-5 shrink-0 transition-transform ${textMuted} ${whatsappOpen ? 'rotate-180' : ''}`}
            />
          </button>
          {whatsappOpen && (
            <div className={`mt-4 space-y-4 pt-4 border-t border-dashed ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
              {WHATSAPP_AGING_BUCKETS.map((bucket) => (
                <div key={bucket.id}>
                  <label className={`block text-xs font-medium mb-1 ${textMuted}`}>{bucket.label}</label>
                  <textarea
                    rows={3}
                    value={notificationForm.whatsappTemplates?.[bucket.id] || ''}
                    onChange={(e) => setNotificationForm((p) => ({
                      ...p,
                      whatsappTemplates: {
                        ...p.whatsappTemplates,
                        [bucket.id]: e.target.value,
                      },
                    }))}
                    className={`w-full rounded-xl border px-3 py-2 text-sm ${inputClass}`}
                    placeholder={DEFAULT_WHATSAPP_TEMPLATES[bucket.id]}
                  />
                </div>
              ))}
            </div>
          )}
        </section>

        <div className={`px-4 py-3 sm:px-5 flex flex-wrap items-center justify-between gap-3 ${sectionMuted} border-t ${divide}`}>
          <Link to="/cobranzas" className="text-xs font-medium text-[#1F80FF] hover:underline">
            Ver bandeja Cobrar
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#1F80FF] text-white hover:bg-[#004AAD] disabled:opacity-50"
          >
            <FiSave className="w-4 h-4" />
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
